import { Link } from "react-router-dom";
import { Zap, Linkedin, Twitter, Instagram, Facebook } from "lucide-react";

const cols = [
  {
    title: "Funding",
    links: [
      { label: "Merchant Cash Advance", href: "/apply" },
      { label: "Working Capital", href: "/apply" },
      { label: "Revenue-Based Financing", href: "/apply" },
      { label: "Line of Credit", href: "/apply" },
      { label: "Equipment Financing", href: "/apply" },
      { label: "SBA Loans", href: "/apply" },
    ],
  },
  {
    title: "Industries",
    links: [
      { label: "Restaurants", href: "/apply" },
      { label: "Trucking", href: "/apply" },
      { label: "Construction", href: "/apply" },
      { label: "Medical", href: "/apply" },
      { label: "Retail", href: "/apply" },
      { label: "E-Commerce", href: "/apply" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/" },
      { label: "Careers", href: "/" },
      { label: "Press", href: "/" },
      { label: "Partners", href: "/" },
      { label: "Resources", href: "/" },
      { label: "Contact", href: "mailto:capital@powerforwardfunding.com", external: true },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Compliance", href: "/terms" },
      { label: "Licensing", href: "/terms" },
      { label: "Disclosures", href: "/terms" },
      { label: "Cookie Policy", href: "/privacy" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="relative bg-[#050505] border-t border-white/5 pt-20 pb-10 overflow-hidden" data-testid="footer">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#0052FF]/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top: brand + CTA */}
        <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-white/5">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0052FF] to-[#00FFA3] grid place-items-center">
                <Zap className="w-6 h-6 text-black" strokeWidth={3} />
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-display font-extrabold text-lg tracking-tight">POWER FORWARD</span>
                <span className="text-[11px] tracking-[0.3em] text-[#00FFA3] font-mono">FUNDING</span>
              </div>
            </div>
            <p className="text-zinc-400 leading-relaxed max-w-md mb-8">
              Power Forward Funding is a direct capital partner deploying $500M+ across small and mid-market businesses nationwide. Built by operators, run by elite advisors.
            </p>
            <div className="space-y-2 text-sm font-mono text-zinc-500">
              <p><span className="text-zinc-700">PHONE</span> <a href="tel:+19086767979" className="text-white hover:text-[#00FFA3]">+1 (908) 676-7979</a></p>
              <p><span className="text-zinc-700">EMAIL</span> <a href="mailto:capital@powerforwardfunding.com" className="text-white hover:text-[#00FFA3]">capital@powerforwardfunding.com</a></p>
              <p><span className="text-zinc-700">HQ</span> <span className="text-white">277 Park Avenue, New York, NY</span></p>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-8">
            {cols.map((c) => (
              <div key={c.title}>
                <p className="text-xs font-mono uppercase tracking-widest text-[#0052FF] mb-4">{c.title}</p>
                <ul className="space-y-3">
                  {c.links.map((l) => (
                    <li key={l.label}>
                      {l.external ? (
                        <a
                          href={l.href}
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="text-sm text-zinc-400 hover:text-white transition-colors"
                        >
                          {l.label}
                        </a>
                      ) : (
                        <Link
                          to={l.href}
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="text-sm text-zinc-400 hover:text-white transition-colors"
                        >
                          {l.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance */}
        <div className="py-10 border-b border-white/5">
          <p className="text-xs text-zinc-600 leading-relaxed max-w-5xl">
            <span className="text-zinc-400 font-semibold">DISCLAIMER:</span> Power Forward Funding is a commercial finance brokerage and direct funder. All loans and advances are made by licensed lenders or non-bank capital partners and are subject to credit approval, underwriting and program terms. Funding amounts, rates and times listed are illustrative and not a guarantee of credit. Power Forward Funding does not provide tax, legal, or accounting advice. State licensing where applicable. NMLS Disclosures available upon request. ©{new Date().getFullYear()} Power Forward Funding, LLC. All rights reserved.
          </p>
        </div>

        {/* Bottom */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <p className="text-xs text-zinc-600 font-mono uppercase tracking-widest">
              Engineered for operators. Built in New York.
            </p>
            <span className="text-zinc-800 hidden md:inline">·</span>
            <Link
              to="/portal/login?staff=1"
              data-testid="footer-employee-login"
              className="text-[11px] text-zinc-700 hover:text-zinc-400 transition-colors lowercase tracking-wide"
              title="Employee access"
            >
              employees
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {[Linkedin, Twitter, Instagram, Facebook].map((Icon, i) => (
              <a
                key={i}
                href="#"
                data-testid={`social-link-${i}`}
                className="w-9 h-9 rounded-full bg-[#111] border border-white/5 grid place-items-center hover:border-[#00FFA3]/40 hover:text-[#00FFA3] transition-colors text-zinc-500"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
